/**
 * KonamiPebble - the konami-code easter egg (#2463).
 *
 * Mount once at the app root. Listens for the classic sequence via
 * useKonamiCode; on trigger, Pebble (the brand-kit SVG mascot rig) slides up
 * from the bottom-right, greets with a speech bubble, throws a small particle
 * burst, then leaves. The ~100KB Pebble rig is lazy-loaded on first trigger,
 * not on page load.
 */

import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { useKonamiCode } from '../hooks/useKonamiCode';
import { trackEvent } from '../../lib/analytics';

const Pebble = lazy(() =>
  import('@smallbiz/brand-kit/components').then((m) => ({ default: m.Pebble }))
);

const BUBBLE_TEXT = "You found me. Now let's build something great.";
const PARTICLE_COUNT = 24;
const PARTICLE_GLYPHS = ['🚀', '✨'];
const PARTICLE_DOT_COLORS = ['#00A6B8', '#D01FB0', '#f5b700'];

type Phase = 'hidden' | 'arriving' | 'greeting' | 'celebrating' | 'settled' | 'leaving';

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mql.matches);
    const handleChange = (event: MediaQueryListEvent) => setReduced(event.matches);
    mql.addEventListener?.('change', handleChange);
    return () => mql.removeEventListener?.('change', handleChange);
  }, []);

  return reduced;
}

type Particle =
  | { id: number; kind: 'emoji'; glyph: string; dx: number; dy: number; delay: number }
  | { id: number; kind: 'dot'; color: string; dx: number; dy: number; delay: number };

function buildParticles(): Particle[] {
  return Array.from({ length: PARTICLE_COUNT }, (_, i) => {
    const angle = (Math.PI * 2 * i) / PARTICLE_COUNT + Math.random() * 0.3;
    const distance = 70 + Math.random() * 60;
    const dx = Math.cos(angle) * distance;
    const dy = Math.sin(angle) * distance;
    const delay = Math.random() * 80;

    if (i % 3 === 0) {
      return {
        id: i,
        kind: 'emoji' as const,
        glyph: PARTICLE_GLYPHS[i % PARTICLE_GLYPHS.length],
        dx,
        dy,
        delay,
      };
    }
    return {
      id: i,
      kind: 'dot' as const,
      color: PARTICLE_DOT_COLORS[i % PARTICLE_DOT_COLORS.length],
      dx,
      dy,
      delay,
    };
  });
}

export function KonamiPebble() {
  const [phase, setPhase] = useState<Phase>('hidden');
  const [particles, setParticles] = useState<Particle[]>([]);
  const reducedMotion = usePrefersReducedMotion();
  const timeoutsRef = useRef<Array<ReturnType<typeof setTimeout>>>([]);

  const clearTimers = useCallback(() => {
    timeoutsRef.current.forEach(clearTimeout);
    timeoutsRef.current = [];
  }, []);

  const schedule = useCallback((fn: () => void, delay: number) => {
    const id = setTimeout(fn, delay);
    timeoutsRef.current.push(id);
  }, []);

  const dismiss = useCallback(() => {
    clearTimers();
    setPhase('hidden');
  }, [clearTimers]);

  const trigger = useCallback(() => {
    clearTimers();
    trackEvent('easter_egg_konami');

    if (reducedMotion) {
      setPhase('settled');
      schedule(() => setPhase('hidden'), 3200);
      return;
    }

    setParticles(buildParticles());
    setPhase('arriving'); // slide up, mood=surprised
    schedule(() => setPhase('greeting'), 550); // mood=happy, bubble shows
    schedule(() => setPhase('celebrating'), 2100); // particle burst
    schedule(() => setPhase('settled'), 2900); // mood=undefined (idle)
    schedule(() => setPhase('leaving'), 4600); // slide away
    schedule(() => setPhase('hidden'), 5200);
  }, [clearTimers, reducedMotion, schedule]);

  useKonamiCode(trigger);

  useEffect(() => {
    if (phase === 'hidden') return undefined;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') dismiss();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [phase, dismiss]);

  useEffect(() => clearTimers, [clearTimers]);

  if (phase === 'hidden') return null;

  const mood =
    phase === 'arriving'
      ? 'surprised'
      : phase === 'greeting' || phase === 'celebrating'
        ? 'happy'
        : undefined;

  const motionClass = reducedMotion
    ? 'konami-pebble-static'
    : phase === 'leaving'
      ? 'konami-pebble-exit'
      : 'konami-pebble-enter';

  return (
    <div
      className="fixed bottom-0 right-0 z-[1000]"
      style={{ pointerEvents: 'none' }}
      data-testid="konami-pebble"
    >
      <style>{KONAMI_STYLES}</style>
      <div
        className={motionClass}
        // Sit above the third-party chat launcher and the scroll-to-top button,
        // which both live in the bottom-right corner.
        style={{ position: 'relative', margin: '0 24px 104px 0', pointerEvents: 'auto' }}
      >
        {(phase === 'greeting' || phase === 'celebrating' || phase === 'settled') && (
          <div
            role="status"
            aria-live="polite"
            className="konami-pebble-bubble"
            style={{
              position: 'absolute',
              bottom: '100%',
              right: 0,
              marginBottom: 10,
              width: 'max-content',
              maxWidth: 220,
              pointerEvents: 'auto',
            }}
          >
            {BUBBLE_TEXT}
          </div>
        )}

        {phase === 'celebrating' && !reducedMotion && (
          <div
            aria-hidden="true"
            style={{
              position: 'absolute',
              inset: 0,
              pointerEvents: 'none',
            }}
          >
            {particles.map((p) => (
              <span
                key={p.id}
                className="konami-pebble-particle"
                style={
                  {
                    '--dx': `${p.dx}px`,
                    '--dy': `${p.dy}px`,
                    animationDelay: `${p.delay}ms`,
                    background: p.kind === 'dot' ? p.color : 'transparent',
                  } as CSSProperties
                }
              >
                {p.kind === 'emoji' ? p.glyph : ''}
              </span>
            ))}
          </div>
        )}

        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss Lurkerbot"
          style={{
            background: 'none',
            border: 0,
            padding: 0,
            cursor: 'pointer',
            display: 'block',
            pointerEvents: 'auto',
          }}
        >
          <Suspense fallback={<div style={{ width: 112, height: 112 }} />}>
            <Pebble size={112} track={!reducedMotion} mood={mood} />
          </Suspense>
        </button>
      </div>
    </div>
  );
}

const KONAMI_STYLES = `
@keyframes konami-pebble-slide-in {
  0% { transform: translate(30px, 140px) scale(0.6); opacity: 0; }
  100% { transform: translate(0, 0) scale(1); opacity: 1; }
}
@keyframes konami-pebble-slide-out {
  0% { transform: translate(0, 0) scale(1); opacity: 1; }
  100% { transform: translate(30px, 160px) scale(0.5); opacity: 0; }
}
@keyframes konami-pebble-particle-burst {
  0% { transform: translate(0, 0) scale(1); opacity: 1; }
  100% { transform: translate(var(--dx), var(--dy)) scale(0.3); opacity: 0; }
}
@keyframes konami-pebble-bubble-in {
  0% { opacity: 0; transform: translateY(6px); }
  100% { opacity: 1; transform: translateY(0); }
}
.konami-pebble-enter { animation: konami-pebble-slide-in 420ms cubic-bezier(0.34, 1.56, 0.64, 1) both; }
.konami-pebble-exit { animation: konami-pebble-slide-out 500ms ease-in both; }
.konami-pebble-static { opacity: 1; transition: opacity 300ms ease; }
.konami-pebble-bubble {
  background: var(--card, #ffffff);
  color: var(--foreground, #1b1622);
  border: 1px solid var(--border, #dcd7d0);
  border-radius: 14px;
  padding: 10px 14px;
  font-size: 13px;
  line-height: 1.4;
  box-shadow: 0 8px 24px -12px rgba(0, 0, 0, 0.35);
  animation: konami-pebble-bubble-in 220ms ease both;
}
.konami-pebble-particle {
  position: absolute;
  top: 40%;
  left: 50%;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  font-size: 16px;
  line-height: 1;
  display: grid;
  place-items: center;
  animation: konami-pebble-particle-burst 900ms ease-out both;
}
@media (prefers-reduced-motion: reduce) {
  .konami-pebble-enter,
  .konami-pebble-exit,
  .konami-pebble-particle,
  .konami-pebble-bubble {
    animation: none !important;
  }
}
`;
